// Client-side grouping of consecutive `email` activities that share a
// non-empty thread_key — the touchpoints list and CompanyDetail's activity
// timeline both render the same "conversation" card via this. Grouping only
// merges ADJACENT rows (as returned by the list, in whatever sort order the
// caller picked) so it composes with both the asc/desc sort toggle and any
// non-email activities interleaved between messages — the latter simply
// break a run rather than being skipped over.
import type { Activity } from '$lib/types';

export type DisplayRow =
  | { kind: 'activity'; activity: Activity }
  | { kind: 'thread'; key: string; messages: Activity[] };

// Stable, unique `{#each}` key for a rendered row. Key on the REPRESENTATIVE
// activity's id, never the thread_key: groupEmailThreads only merges ADJACENT
// same-thread rows, so a conversation whose messages are non-adjacent in the
// (chronological) list produces multiple thread entries that share one
// thread_key. Keying those by `t:${thread_key}` made them collide, and Svelte's
// keyed-{#each} duplicate-key guard (each.js each_key_duplicate) THROWS on the
// collision, aborting the entire list render — a blank/unresponsive page that
// only appears once a page holds two+ emails of the same conversation
// (email-majority pages: team inbox, Hotels touchpoints). Every activity belongs
// to exactly one entry, so its id is unique across the list and collision-free.
export function displayRowKey(entry: DisplayRow): string {
  return entry.kind === 'thread' ? `r:${entry.messages[0].id}` : `r:${entry.activity.id}`;
}

export function groupEmailThreads(rows: Activity[]): DisplayRow[] {
  const out: DisplayRow[] = [];
  for (const row of rows) {
    const key = row.type === 'email' ? row.email?.thread_key : '';
    const last = out[out.length - 1];
    if (key && last?.kind === 'thread' && last.key === key) {
      last.messages.push(row);
    } else if (key) {
      out.push({ kind: 'thread', key, messages: [row] });
    } else {
      out.push({ kind: 'activity', activity: row });
    }
  }
  return out;
}

function effectiveTime(a: Activity): number {
  const t = a.email?.sent_at ?? a.created_at;
  return t ? new Date(t).getTime() : 0;
}

export function newestMessage(messages: Activity[]): Activity {
  return messages.reduce((a, b) => (effectiveTime(b) > effectiveTime(a) ? b : a));
}

export function sortMessagesDesc(messages: Activity[]): Activity[] {
  return [...messages].sort((a, b) => effectiveTime(b) - effectiveTime(a));
}

// Unique From addresses across a thread, capped for display.
export function threadParticipants(messages: Activity[]): string {
  const seen = new Set<string>();
  for (const m of messages) {
    if (m.email?.from_addr) seen.add(m.email.from_addr);
  }
  const arr = [...seen];
  if (arr.length <= 2) return arr.join(', ');
  return `${arr.slice(0, 2).join(', ')} +${arr.length - 2} more`;
}
